import { useQuery } from '@tanstack/react-query'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import Switch from 'ext-ui/components/Switch/Switch'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import { text_colorTextSecondary } from 'ext-ui/style/colors'
import AlertCircleIcon from 'ext-ui/svg/icon/AlertCircleIcon'
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import { useTranslate } from 'src/localization/Trns'
import { mccGroupsQueries } from 'src/queries/mccGroups.queries'

import SettingsControlRow from '../SettingsControlRow/SettingsControlRow'
import SettingsDisclosure from '../SettingsDisclosure/SettingsDisclosure'
import SettingsSegmented from '../SettingsSegmented/SettingsSegmented'
import SettingsTile from '../SettingsTile/SettingsTile'
import style, { flyoutStyle } from './SpendRestrictionsTile.style'

const PILL_CAP = 4

/**
 * A single MCC (`to` omitted) or an inclusive range. Structural rather than a
 * pre-joined `'7800\u20137828'` string: sniffing the display text for an en dash
 * made the punctuation load-bearing, so a plain hyphen typed while editing the
 * table \u2014 indistinguishable at a glance \u2014 would silently reclassify a range as
 * a single code, surfacing only as a wrong count in the toggle label.
 */
type MccCodeEntry = { from: string; to?: string; label?: string }

type MccCategory = { id: string; name: string }

function isRange(entry: MccCodeEntry): boolean {
  return entry.to !== undefined
}

function codeKey(entry: MccCodeEntry): string {
  return entry.to === undefined ? entry.from : `${entry.from}-${entry.to}`
}

function formatCode(entry: MccCodeEntry): string {
  return entry.to === undefined ? entry.from : `${entry.from}\u2013${entry.to}`
}

/**
 * Last-resort casing for a group id we have no curated name for. `/mccgroups`
 * serves `group` as `msg(mcc.key)` and every `mcc.group.*` message is upper-case
 * in every locale (`CAR RENTAL`; fr-CA `COMPAGNIE AÉRIENNE`), and shouting is
 * wrong here — worst inside the tile's prose summary.
 *
 * Deliberately not the primary path. English title case is only correct for
 * English: fr-CA wants sentence case, so this would render "Commande Par
 * Téléphone Ou Courrier". `MCC_GROUP_NAMES` is preferred precisely so that
 * casing a localized string is the exception rather than the rule, and both go
 * away once the API serves a display name.
 */
function titleCaseGroupName(name: string): string {
  return name
    .split(' ')
    .map((word) =>
      word.length === 0
        ? word
        : word.charAt(0).toLocaleUpperCase() +
          word.slice(1).toLocaleLowerCase(),
    )
    .join(' ')
}

/**
 * The display name for each group id, and the preferred source for it.
 *
 * `/mccgroups` only serves the short upper-case `group` today, and title-casing
 * it yields a name that differs from this table for 11 of the 22 groups
 * (`Financial Service Providers` here vs `Financial` from the wire). Since one
 * of those eleven is a seeded exception, deriving the name from the response
 * made the label visibly change on every load as the query resolved. Preferring
 * this table keeps the pre- and post-resolve name identical.
 *
 * The trade is deliberate: these are English, so a non-English locale sees
 * English here rather than an upper-case translation. The API is arriving at
 * the same place — the display name it is gaining ships English-only until
 * translation catches up — so this is early, not divergent. Delete the table
 * when the response carries the name itself.
 */
const MCC_GROUP_NAMES: Record<string, string> = {
  AIRLINE: 'Airline',
  ENTERTAINMENT: 'Amusement and Entertainment',
  ASSOCIATIONS: 'Associations',
  AUTOMOBILES: 'Automobiles and Vehicles',
  BUSINESS: 'Business Services',
  CAR_RENTAL: 'Car Rental',
  EDUCATION: 'Education',
  FINANCIAL: 'Financial Service Providers',
  GAS: 'Gas Stations',
  GOVERNMENT: 'Government Services',
  LODGING: 'Lodging',
  MAIL_PHONE_ORDER: 'Mail Phone Order',
  PERSONAL: 'Personal Services',
  PROFESSIONAL: 'Professional Services',
  REPAIR: 'Repair Services',
  RESTAURANTS: 'Restaurants',
  RETAIL: 'Retail Stores',
  TELECOM: 'Telecom',
  TRANSPORTATION: 'Transportation',
  UTILITIES: 'Utilities',
  WHOLESALE: 'Wholesale Trade',
  OTHER: 'Other',
}

/**
 * The code ranges behind each group, transcribed from the API's MCC group
 * resources. Temporary: `/mccgroups` serves only `{ id, group }` today, so there
 * is nothing to read the ranges from. Delete this once the catalog endpoint
 * carries them. Keyed by group id, so it survives localization of the name.
 */
const MCC_CODES_BY_GROUP_ID: Record<string, MccCodeEntry[]> = {
  AIRLINE: [
    { from: '3000', to: '3299' },
    { from: '4511', label: 'Airlines, Air Carriers (not listed elsewhere)' },
  ],
  ENTERTAINMENT: [
    { from: '4411', label: 'Cruise and Steamship Lines' },
    { from: '7829', to: '7999' },
  ],
  ASSOCIATIONS: [
    { from: '8398', to: '8699' },
    { from: '7800', to: '7828' },
  ],
  AUTOMOBILES: [
    { from: '5013', to: '5020' },
    { from: '5511', to: '5599' },
  ],
  BUSINESS: [
    { from: '0742', to: '0780' },
    { from: '1520', to: '1799' },
    { from: '2741', to: '2842' },
    { from: '7311', to: '7399' },
  ],
  CAR_RENTAL: [
    { from: '3351', to: '3441' },
    { from: '7512', to: '7519' },
  ],
  EDUCATION: [{ from: '8211', to: '8299' }],
  FINANCIAL: [
    { from: '4829', label: 'Money Orders \u2013 Wire Transfer' },
    { from: '6010', to: '6760' },
  ],
  GAS: [{ from: '5541', to: '5542' }],
  GOVERNMENT: [{ from: '9211', to: '9950' }],
  LODGING: [
    { from: '3501', to: '3831' },
    { from: '7011', to: '7033' },
  ],
  MAIL_PHONE_ORDER: [{ from: '5960', to: '5969' }],
  PERSONAL: [
    { from: '5960', to: '5969' },
    { from: '7210', to: '7299' },
  ],
  PROFESSIONAL: [
    { from: '5933', label: 'Pawn Shops and Salvage Yards' },
    { from: '8011', to: '8111' },
    { from: '8351', label: 'Child Care Services' },
    // The source lists a bare 8734 as well as 8734\u20138743, which contains it.
    // Kept only as the range: carrying both rendered 8734 twice and counted it
    // twice in the toggle label. The overlap is upstream, not a transcription
    // slip \u2014 one of several the catalog endpoint is expected to resolve.
    { from: '8734', to: '8743' },
    { from: '8911', to: '9223' },
  ],
  REPAIR: [{ from: '7531', to: '7699' }],
  RESTAURANTS: [{ from: '5811', to: '5814' }],
  RETAIL: [
    { from: '7841', label: 'Video Tape Rental Stores' },
    { from: '5200', to: '5510' },
    { from: '5600', to: '5999' },
    { from: '7230', to: '7261' },
  ],
  TELECOM: [{ from: '4812', to: '4821' }],
  TRANSPORTATION: [
    { from: '4011', to: '4225' },
    { from: '4457', to: '4468' },
    { from: '4582', label: 'Airports, Airport Terminals, Flying Fields' },
    { from: '4722', to: '4789' },
    { from: '7523', to: '7524' },
  ],
  UTILITIES: [
    { from: '4899', to: '4900' },
    {
      from: '5983',
      label: 'Fuel \u2013 Fuel Oil, Wood, Coal, Liquefied Petroleum',
    },
  ],
  WHOLESALE: [
    { from: '5021', to: '5199' },
    { from: '0743', to: '0744' },
  ],
}

// Shown only until `/mccgroups` resolves, so the drawer is never empty. Same
// taxonomy as the API, just not localized.
const FALLBACK_MERCHANT_CATEGORIES: MccCategory[] = Object.entries(
  MCC_GROUP_NAMES,
).map(([id, name]) => ({ id, name }))

const GEOFENCE_COUNTRIES = [
  'United States',
  'Canada',
  'United Kingdom',
  'France',
  'Germany',
  'Australia',
  'Japan',
]

type MccMode = 'allowAllExcept' | 'blockAllExcept'

type ChannelKey = 'online' | 'swipe' | 'atm' | 'wallet'

function cappedPills(pills: ReactNode[]): ReactNode[] {
  if (pills.length <= PILL_CAP) {
    return pills
  }
  return [
    ...pills.slice(0, PILL_CAP),
    <Pill key="more" size="small">{`+${pills.length - PILL_CAP} more`}</Pill>,
  ]
}

/**
 * The one place the underlying Mastercard codes are visible — the tile summary
 * deliberately stays clean category names only.
 */
function MccCodeExpander(props: {
  groupId: string
  groupName: string
}): ReactElement | null {
  const { groupId, groupName } = props
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const listId = useId()
  const codes = MCC_CODES_BY_GROUP_ID[groupId]

  if (!codes) {
    return null
  }

  const count = String(codes.length)
  const rangeCount = codes.filter(isRange).length
  let label: string
  if (rangeCount === 0) {
    label =
      codes.length === 1
        ? translate('1 code')
        : translate('${count} codes', { count })
  } else if (rangeCount === codes.length) {
    label =
      codes.length === 1
        ? translate('1 range')
        : translate('${count} ranges', { count })
  } else {
    label = translate('${count} codes & ranges', { count })
  }

  return (
    <div className="SpendRestrictionsTile-codes">
      <button
        type="button"
        className="SpendRestrictionsTile-codesToggle"
        aria-expanded={open}
        // The count alone is not a usable name: against the real catalog seven
        // groups are "1 range" and six are "2 ranges", so a search that matches
        // several renders a wall of identically-named buttons. The visible text
        // stays the bare count; only the accessible name is qualified.
        aria-label={translate('Merchant category codes for ${name}: ${label}', {
          name: groupName,
          label,
        })}
        // The list is unmounted while collapsed, so only advertise the controls
        // relationship when the target exists — as SettingsDisclosure does.
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((prev) => !prev)}
      >
        {label}
      </button>
      {open && (
        <ul
          id={listId}
          className="SpendRestrictionsTile-codeList"
          aria-label={translate('Merchant category codes for ${name}', {
            name: groupName,
          })}
        >
          {codes.map((entry) => (
            <li key={codeKey(entry)} className="SpendRestrictionsTile-codeRow">
              <span className="SpendRestrictionsTile-code">
                {formatCode(entry)}
              </span>
              {entry.label && (
                <span className="SpendRestrictionsTile-codeLabel">
                  {entry.label}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function MerchantCategoriesDrawer(props: {
  open: boolean
  onClose: () => void
  mode: MccMode
  categories: MccCategory[]
  /** Group ids, not display names — the API localizes `group`. */
  exceptions: string[]
  /** Owned by the tile so there is one resolver, not one per consumer. */
  resolveName: (groupId: string) => string
  onMode: (mode: MccMode) => void
  onAdd: (groupId: string) => void
  onRemove: (groupId: string) => void
}): ReactElement {
  const {
    open,
    onClose,
    mode,
    categories,
    exceptions,
    resolveName,
    onMode,
    onAdd,
    onRemove,
  } = props
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  // The drawer stays mounted across open/close, so clear the search each time it
  // closes — otherwise it reopens pre-filtered to the previous query.
  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const addVerb =
    mode === 'allowAllExcept' ? translate('block') : translate('allow')
  const listLabel =
    mode === 'allowAllExcept' ? translate('Blocked') : translate('Allowed')
  const addable = categories.filter(
    (category) =>
      !exceptions.includes(category.id) &&
      category.name.toLowerCase().includes(query.trim().toLowerCase()),
  )
  return (
    <BaseFlyout
      className="SpendRestrictionsDrawer"
      css={flyoutStyle}
      isOpen={open}
      onClose={onClose}
      aria-label={translate('Merchant categories')}
    >
      {/*
        The heading is content-owned rather than BaseFlyout's `title`/`subtitle`.
        Those render a raised header band with a 32px h2, which splits the panel
        into two surfaces; the design is one flat surface under a close-X with a
        20px heading.
      */}
      <div className="SpendRestrictionsTile-drawer" css={style}>
        <div className="SpendRestrictionsTile-drawerHeading">
          <h2>{translate('Merchant categories')}</h2>
          <p className="SpendRestrictionsTile-drawerIntro">
            {translate(
              'Choose where this card can be used. These controls are a preview — nothing is saved and no restriction is enforced yet.',
            )}
          </p>
          <p className="SpendRestrictionsTile-drawerLead">
            {translate(
              'Each category groups a set of Mastercard merchant category codes (MCCs).',
            )}
          </p>
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {translate('Mode')}
          </div>
          <SettingsSegmented
            ariaLabel={translate('Merchant-category default')}
            value={mode}
            options={[
              {
                label: translate('Allow all except'),
                value: 'allowAllExcept',
              },
              {
                label: translate('Block all except'),
                value: 'blockAllExcept',
              },
            ]}
            onChange={(next) => onMode(next as MccMode)}
          />
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {translate('Add a category to ${verb}', { verb: addVerb })}
          </div>
          <ExtUITextInput
            variant="small"
            label={translate('Search categories')}
            placeholder={translate('Type a category name…')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim() !== '' && (
            <ul className="SpendRestrictionsTile-addList">
              {addable.length === 0 ? (
                <li className="SpendRestrictionsTile-addEmpty">
                  {translate('No matches')}
                </li>
              ) : (
                addable.map((category) => (
                  <li
                    key={category.id}
                    className="SpendRestrictionsTile-addItem"
                  >
                    <button
                      type="button"
                      className="SpendRestrictionsTile-addRow"
                      onClick={() => {
                        onAdd(category.id)
                        setQuery('')
                      }}
                    >
                      <span>{category.name}</span>
                      <span aria-hidden="true">{`+ ${addVerb}`}</span>
                    </button>
                    <div className="SpendRestrictionsTile-addCodes">
                      <MccCodeExpander
                        groupId={category.id}
                        groupName={category.name}
                      />
                    </div>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker forList">
            {exceptions.length > 0
              ? `${listLabel} · ${exceptions.length}`
              : listLabel}
          </div>
          {exceptions.length === 0 ? (
            <p className="SpendRestrictionsTile-drawerEmpty">
              {mode === 'allowAllExcept'
                ? translate('No exceptions — every category is allowed.')
                : translate('No exceptions — every category is blocked.')}
            </p>
          ) : (
            <ul className="SpendRestrictionsTile-exceptionList">
              {exceptions.map((groupId) => (
                <li
                  key={groupId}
                  className="SpendRestrictionsTile-exceptionRow"
                >
                  {/* A div, not a span: MccCodeExpander renders flow content. */}
                  <div className="SpendRestrictionsTile-exceptionMain">
                    <span className="SpendRestrictionsTile-exceptionName">
                      {resolveName(groupId)}
                    </span>
                    <MccCodeExpander
                      groupId={groupId}
                      groupName={resolveName(groupId)}
                    />
                  </div>
                  <span className="SpendRestrictionsTile-exceptionControl">
                    <Pill
                      size="small"
                      color={mode === 'allowAllExcept' ? 'severe' : 'highlight'}
                    >
                      {listLabel}
                    </Pill>
                    <button
                      type="button"
                      className="SpendRestrictionsTile-removeButton"
                      aria-label={translate('Remove ${name}', {
                        name: resolveName(groupId),
                      })}
                      onClick={() => onRemove(groupId)}
                    >
                      {'×'}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="SpendRestrictionsTile-drawerFooter">
          {/*
            Not a literal ⓘ (U+24D8): it was the only one in the codebase, and
            it renders at whatever weight and baseline the first font in the
            stack happens to have for it — some Windows fallbacks have none.
            Secondary-text fill, not the icon's red default, since this note is
            informational rather than a warning.
          */}
          <AlertCircleIcon
            className="SpendRestrictionsTile-drawerFooterIcon"
            fill={text_colorTextSecondary}
          />
          <p>
            {mode === 'allowAllExcept'
              ? translate('Everything else is allowed. Nothing is saved yet.')
              : translate('Everything else is blocked. Nothing is saved yet.')}
          </p>
        </div>
      </div>
    </BaseFlyout>
  )
}

function GeofenceDrawer(props: {
  open: boolean
  onClose: () => void
  countries: string[]
  onAdd: (country: string) => void
  onRemove: (country: string) => void
}): ReactElement {
  const { open, onClose, countries, onAdd, onRemove } = props
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  // Clear the search when the drawer closes so it reopens fresh (it stays
  // mounted across open/close).
  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const addable = GEOFENCE_COUNTRIES.filter(
    (country) =>
      !countries.includes(country) &&
      country.toLowerCase().includes(query.trim().toLowerCase()),
  )

  return (
    <BaseFlyout
      isOpen={open}
      onClose={onClose}
      className="SpendRestrictionsDrawer"
      css={flyoutStyle}
      aria-label={translate('Geofence')}
    >
      <div className="SpendRestrictionsTile-drawer" css={style}>
        <div className="SpendRestrictionsTile-drawerHeading">
          <h2>{translate('Geofence')}</h2>
          <p className="SpendRestrictionsTile-drawerIntro">
            {translate(
              'Choose where this card can be used. These controls are a preview — nothing is saved and no restriction is enforced yet.',
            )}
          </p>
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <ExtUITextInput
            variant="small"
            label={translate('Add a country')}
            placeholder={translate('Search countries…')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim() !== '' && (
            <ul className="SpendRestrictionsTile-addList">
              {addable.length === 0 ? (
                <li className="SpendRestrictionsTile-addEmpty">
                  {translate('No matches')}
                </li>
              ) : (
                addable.map((country) => (
                  <li key={country}>
                    <button
                      type="button"
                      className="SpendRestrictionsTile-addRow"
                      onClick={() => {
                        onAdd(country)
                        setQuery('')
                      }}
                    >
                      <span>{country}</span>
                      <span aria-hidden="true">{translate('+ add')}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {countries.length > 0
              ? `${translate('Permitted')} · ${countries.length}`
              : translate('Permitted')}
          </div>
          {countries.length === 0 ? (
            <p className="SpendRestrictionsTile-drawerEmpty">
              {translate('No regions — the card is blocked worldwide.')}
            </p>
          ) : (
            <ul className="SpendRestrictionsTile-exceptionList">
              {countries.map((country) => (
                <li
                  key={country}
                  className="SpendRestrictionsTile-exceptionRow"
                >
                  <span className="SpendRestrictionsTile-exceptionName">
                    {country}
                  </span>
                  <button
                    type="button"
                    className="SpendRestrictionsTile-removeButton"
                    aria-label={translate('Remove ${name}', { name: country })}
                    onClick={() => onRemove(country)}
                  >
                    {'×'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BaseFlyout>
  )
}

type SpendRestrictionsTileProps = {
  /**
   * Admin/Owner. The deliverable makes merchant categories view-only for Card
   * Managers; payment channels and geofence carry no such guardrail, so they
   * stay editable for everyone until design says otherwise.
   */
  canEditMerchantCategories: boolean
}

/**
 * "Where & how it can spend" tile (EX-40354) — merchant categories, payment
 * channels, and geofence controls. Visual-only with placeholder data; edits are
 * held in local state and not persisted.
 */
export default function SpendRestrictionsTile(
  props: SpendRestrictionsTileProps,
): ReactElement {
  const { canEditMerchantCategories } = props
  const translate = useTranslate()

  // Reuses the same `/mccgroups` catalog the transaction filter, automation-rule
  // form, and custom-field detail already read, so this surface adds no new
  // query family. Read raw so nothing here depends on the legacy MccGroup class.
  const { data: mccGroupData } = useQuery(mccGroupsQueries.list())
  const merchantCategories = useMemo<MccCategory[]>(() => {
    const groups =
      mccGroupData?.mccGroups.map((group) => ({
        id: group.id,
        // Curated name first — see MCC_GROUP_NAMES. The wire value is upper-case
        // and only gets cased here when the catalog carries an id we don't know.
        name: MCC_GROUP_NAMES[group.id] ?? titleCaseGroupName(group.group),
      })) ?? []
    return groups.length > 0 ? groups : FALLBACK_MERCHANT_CATEGORIES
  }, [mccGroupData])

  const [mccMode, setMccMode] = useState<MccMode>('allowAllExcept')
  // Group ids, so the list survives the API localizing its display names. The
  // design seeds "Gambling" and "Cash advance / ATM"; neither is a group in our
  // taxonomy — those codes sit under ASSOCIATIONS (7800–7828) and FINANCIAL
  // (6010–6760). Placeholder either way until save/retrieval lands.
  const [mccExceptions, setMccExceptions] = useState<string[]>([
    'ASSOCIATIONS',
    'FINANCIAL',
  ])
  const [mccDrawerOpen, setMccDrawerOpen] = useState(false)

  const categoryNameById = useMemo(
    () => new Map(merchantCategories.map((c) => [c.id, c.name])),
    [merchantCategories],
  )
  // The single name resolver — passed to the drawer rather than reimplemented
  // there, so the fallback chain only exists once.
  const nameForGroup = useCallback(
    (groupId: string): string =>
      categoryNameById.get(groupId) ?? MCC_GROUP_NAMES[groupId] ?? groupId,
    [categoryNameById],
  )

  const [channels, setChannels] = useState<Record<ChannelKey, boolean>>({
    online: true,
    swipe: true,
    atm: false,
    wallet: true,
  })

  const [countries, setCountries] = useState<string[]>([
    'United States',
    'Canada',
  ])
  const [geofenceDrawerOpen, setGeofenceDrawerOpen] = useState(false)

  const channelRows: {
    key: ChannelKey
    label: string
    shortLabel: string
    description: string
  }[] = [
    {
      key: 'online',
      label: translate('Online & e-commerce'),
      shortLabel: translate('Online'),
      description: translate('Card-not-present purchases'),
    },
    {
      key: 'swipe',
      label: translate('In-store (swipe / tap)'),
      shortLabel: translate('In-store'),
      description: translate('Card-present terminals'),
    },
    {
      key: 'atm',
      label: translate('ATM withdrawals'),
      shortLabel: translate('ATM'),
      description: translate('Cash access'),
    },
    {
      key: 'wallet',
      label: translate('Mobile wallet'),
      shortLabel: translate('Mobile wallet'),
      description: translate('Apple Pay / Google Pay'),
    },
  ]

  const mccSummary =
    mccExceptions.length === 0 ? (
      <span className="SpendRestrictionsTile-summaryText">
        {mccMode === 'allowAllExcept'
          ? translate('All categories allowed')
          : translate('All categories blocked')}
      </span>
    ) : (
      <span className="SpendRestrictionsTile-summaryPills">
        {/*
          Leads the pills rather than trailing them. The old trailing
          `translate('only')` was a context-free fragment for translators, and it
          landed after the "+N more" pill once the list was capped ("A B C D +2
          more only"). A prefix is a whole translatable phrase, and it keeps the
          mode off colour alone — the pill hue is the only other signal.
        */}
        <span className="SpendRestrictionsTile-summaryText">
          {mccMode === 'allowAllExcept'
            ? translate('Blocked:')
            : translate('Allowed only:')}
        </span>
        {cappedPills(
          mccExceptions.map((groupId) => (
            <Pill
              key={groupId}
              size="small"
              color={mccMode === 'allowAllExcept' ? 'severe' : 'highlight'}
            >
              {nameForGroup(groupId)}
            </Pill>
          )),
        )}
      </span>
    )

  // The state of the rule and the preview disclaimer are separate sentences,
  // because the disclaimer is about editing — it has nothing to say to a Card
  // Manager, who cannot edit this from here at all.
  const mccList = mccExceptions.map(nameForGroup).join(', ')
  let mccBody: string
  if (mccExceptions.length === 0) {
    mccBody =
      mccMode === 'allowAllExcept'
        ? translate('All categories allowed.')
        : translate('All categories blocked.')
  } else if (mccMode === 'allowAllExcept') {
    mccBody = translate(
      'Allows everywhere except ${list}. Everything else is allowed.',
      { list: mccList },
    )
  } else {
    mccBody = translate('Allows only ${list}. Everything else is blocked.', {
      list: mccList,
    })
  }

  const channelSummary = (
    <span className="SpendRestrictionsTile-summaryPills">
      {cappedPills(
        [...channelRows]
          .sort((a, b) => Number(channels[b.key]) - Number(channels[a.key]))
          .map((row) => (
            <Pill
              key={row.key}
              size="small"
              color={channels[row.key] ? 'saas' : 'severe'}
            >
              {row.shortLabel}
            </Pill>
          )),
      )}
    </span>
  )

  const geofenceSummary =
    countries.length === 0 ? (
      <span className="SpendRestrictionsTile-summaryText">
        {translate('Worldwide blocked')}
      </span>
    ) : (
      <span className="SpendRestrictionsTile-summaryPills">
        {cappedPills(
          countries.map((country) => (
            <Pill key={country} size="small">
              {country}
            </Pill>
          )),
        )}
      </span>
    )

  const addMccException = (name: string): void =>
    setMccExceptions((prev) => (prev.includes(name) ? prev : [...prev, name]))
  const removeMccException = (name: string): void =>
    setMccExceptions((prev) => prev.filter((item) => item !== name))
  const addCountry = (country: string): void =>
    setCountries((prev) => (prev.includes(country) ? prev : [...prev, country]))
  const removeCountry = (country: string): void =>
    setCountries((prev) => prev.filter((item) => item !== country))

  return (
    <SettingsTile title={translate('Where & how it can spend')}>
      <div className="SpendRestrictionsTile" css={style}>
        <p className="SpendRestrictionsTile-intro">
          {translate(
            'Where and how this card can be used. These controls are a preview — edits stay on this screen, are not saved, and change nothing on the card. Tap a row to expand.',
          )}
        </p>

        <SettingsDisclosure
          title={translate('Merchant categories')}
          summary={mccSummary}
          // "View" rather than "Edit" when the row opens onto nothing editable.
          actionLabel={
            canEditMerchantCategories ? translate('Edit') : translate('View')
          }
        >
          <p className="SpendRestrictionsTile-body">{mccBody}</p>
          {canEditMerchantCategories ? (
            <>
              <p className="SpendRestrictionsTile-body">
                {translate('Edits here are not saved yet.')}
              </p>
              <Button
                variant="secondary"
                dropShadow={false}
                onClick={() => setMccDrawerOpen(true)}
                data-testid="edit-merchant-categories"
              >
                {translate('Edit categories')}
              </Button>
            </>
          ) : (
            <p
              className="SpendRestrictionsTile-body"
              data-testid="merchant-categories-view-only"
            >
              {translate(
                'Only an Admin or Owner can change merchant categories.',
              )}
            </p>
          )}
        </SettingsDisclosure>

        <SettingsDisclosure
          title={translate('Payment channels')}
          summary={channelSummary}
          actionLabel={translate('Edit')}
        >
          <p className="SpendRestrictionsTile-body">
            {translate(
              'Disable a channel to shrink the fraud surface. Turned-off channels decline in real time.',
            )}
          </p>
          {channelRows.map((row) => (
            <SettingsControlRow
              key={row.key}
              title={row.label}
              description={row.description}
            >
              <Switch
                checked={channels[row.key]}
                ariaLabel={row.label}
                onCheckedChange={(checked) =>
                  setChannels((prev) => ({ ...prev, [row.key]: checked }))
                }
                data-testid={`channel-${row.key}`}
              />
            </SettingsControlRow>
          ))}
        </SettingsDisclosure>

        <SettingsDisclosure
          title={translate('Geofence')}
          summary={geofenceSummary}
          actionLabel={translate('Edit')}
        >
          <p className="SpendRestrictionsTile-body">
            {translate(
              'Approvals outside the permitted regions are declined. All other countries are blocked.',
            )}
          </p>
          <div className="SpendRestrictionsTile-expandedPills">
            {geofenceSummary}
          </div>
          <Button
            variant="secondary"
            dropShadow={false}
            onClick={() => setGeofenceDrawerOpen(true)}
            data-testid="edit-geofence"
          >
            {translate('Edit countries')}
          </Button>
        </SettingsDisclosure>
      </div>

      <MerchantCategoriesDrawer
        open={mccDrawerOpen}
        onClose={() => setMccDrawerOpen(false)}
        mode={mccMode}
        categories={merchantCategories}
        exceptions={mccExceptions}
        resolveName={nameForGroup}
        onMode={setMccMode}
        onAdd={addMccException}
        onRemove={removeMccException}
      />
      <GeofenceDrawer
        open={geofenceDrawerOpen}
        onClose={() => setGeofenceDrawerOpen(false)}
        countries={countries}
        onAdd={addCountry}
        onRemove={removeCountry}
      />
    </SettingsTile>
  )
}
